import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AbPostHog } from 'app/abstract/services/posthog.abstract';
import { AbTenant } from 'app/abstract/full-pages/admin/tenant.abstract';
import { AuthService } from 'app/shared/auth/auth.service';
import {
  IdentificacaoPostHog,
  UsuarioIdentificavel,
  montarIdentificacaoPostHog
} from './posthog-identificacao';
import { identidadeJaAplicada, registrarIdentidadeAplicada } from './posthog-identidade';

@Injectable({ providedIn: 'root' })
export class IdentificacaoPostHogService {
  constructor(
    private posthog: AbPostHog,
    private authService: AuthService,
    private tenantService: AbTenant,
    private translate: TranslateService
  ) {}

  /**
   * Identifica a partir da sessão persistida. Roda a cada montagem da folha, então só
   * envia quando a identidade mudou de fato.
   */
  identificarDoStorage(): void {
    const user = this.authService.getUsuarioStorage();
    if (!user) {
      return;
    }
    const ident = this.montar(user, this.tenantService.getTenantStorage()?.id);
    if (!ident || identidadeJaAplicada(ident)) {
      return;
    }
    this.aplicar(ident);
  }

  /**
   * Identifica numa transição explícita de identidade (login, troca de conta). Sempre
   * envia: quem chama acabou de mudar quem está autenticado.
   */
  identificar(user: UsuarioIdentificavel, tenantId: number | undefined): void {
    const ident = this.montar(user, tenantId);
    if (!ident) {
      return;
    }
    this.aplicar(ident);
  }

  private montar(
    user: UsuarioIdentificavel,
    tenantId: number | undefined
  ): IdentificacaoPostHog | null {
    return montarIdentificacaoPostHog(user, tenantId, this.resolverIdioma());
  }

  private aplicar(ident: IdentificacaoPostHog): void {
    registrarIdentidadeAplicada(ident);
    this.posthog.identify(ident.distinctId, ident.personProps);
    this.posthog.register(ident.globalProps);
  }

  /**
   * O locale persistido é o fallback porque os APP_INITIALIZER não têm ordem garantida
   * entre si: na folha, a identificação pode rodar antes da hidratação do idioma, e o
   * TranslateService ainda não teria idioma corrente nem default.
   */
  private resolverIdioma(): string | undefined {
    return (
      this.translate.currentLang ||
      this.translate.defaultLang ||
      this.authService.getLocale() ||
      undefined
    );
  }
}
